import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import CalculatorShell from "@/components/tools/CalculatorShell";
import { NumberInput, SelectInput, RadioGroup, Toggle } from "@/components/tools/Inputs";
import ResultsCard from "@/components/tools/ResultsCard";
import { useProducts } from "@/hooks/useProducts";
import { postTool } from "@/lib/api";
import { buildApplyQuery } from "@/lib/format";

const GOALS = [
  { value: "protect-family", label: "Protect my family" },
  { value: "save-retirement", label: "Save for retirement" },
  { value: "tax-advantaged-growth", label: "Tax-advantaged growth" },
  { value: "plan-college", label: "Plan for college" },
  { value: "healthcare-costs", label: "Prepare for healthcare costs" },
  { value: "leave-legacy", label: "Leave a legacy" },
];

const MARITAL = [
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "partnered", label: "Partnered" },
  { value: "divorced", label: "Divorced" },
  { value: "widowed", label: "Widowed" },
];

const RISK = [
  { value: "conservative", label: "Conservative" },
  { value: "balanced", label: "Balanced" },
  { value: "growth", label: "Growth" },
];

const EMPLOYMENT = [
  { value: "w2", label: "W-2 Employee" },
  { value: "self-employed-1099", label: "1099 / Self-Employed" },
  { value: "business-owner", label: "Business Owner" },
  { value: "mixed", label: "Mixed (W-2 + side income)" },
  { value: "retired", label: "Retired" },
];

const PROFESSION = [
  { value: "physician-surgeon", label: "Physician / Surgeon" },
  { value: "dentist", label: "Dentist" },
  { value: "attorney", label: "Attorney" },
  { value: "tech-engineering", label: "Tech / Engineering" },
  { value: "finance-banking", label: "Finance / Banking" },
  { value: "business-owner", label: "Business Owner" },
  { value: "educator", label: "Educator" },
  { value: "healthcare-non-md", label: "Healthcare (Non-MD)" },
  { value: "government", label: "Government" },
  { value: "other", label: "Other / Prefer not to say" },
];

const PARTNERS = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes" },
  { value: "na", label: "Not applicable" },
];

const ESTATE = [
  { value: "no", label: "No" },
  { value: "maybe", label: "Maybe — open to learning" },
  { value: "yes", label: "Yes — actively planning" },
];

const NET_WORTH = [
  { value: "lt-250k", label: "Under $250K" },
  { value: "250k-1m", label: "$250K – $1M" },
  { value: "1m-3m", label: "$1M – $3M" },
  { value: "3m-10m", label: "$3M – $10M" },
  { value: "10m-plus", label: "$10M+" },
];

export default function Recommender() {
  const { products } = useProducts();
  const [form, setForm] = useState({
    age: 35,
    marital_status: "married",
    dependents: 2,
    annual_income: 120000,
    primary_goal: "protect-family",
    has_existing_life: false,
    risk_tolerance: "balanced",
    employment_type: "w2",
    profession: "other",
    has_business_partners: "no",
    estate_planning_concern: "no",
    net_worth_bucket: "lt-250k",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.age === "" || form.age < 0 || form.age > 100) e.age = "Age must be 0–100.";
    if (form.dependents === "" || form.dependents < 0) e.dependents = "Must be 0 or more.";
    if (form.annual_income === "" || form.annual_income < 0)
      e.annual_income = "Must be 0 or more.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setServerError("");
    setResult(null);
    try {
      await new Promise((r) => setTimeout(r, 500));
      const data = await postTool("recommender", form);
      setResult(data);
    } catch (err) {
      setServerError("We couldn't calculate that. Please check your inputs and try again.");
    } finally {
      setLoading(false);
    }
  };

  const reasonsByProduct = result
    ? Object.fromEntries(result.matches.map((m) => [m.product_id, m.reasons]))
    : {};

  return (
    <CalculatorShell
      title="Personalized Product Recommender"
      blurb="Answer a few quick questions and we'll match the right products from our 17-product catalog to your stage of life."
      duration="Takes ~90 seconds"
      Icon={Sparkles}
    >
      <form
        onSubmit={onSubmit}
        data-testid="recommender-form"
        className="bg-white rounded-2xl border border-lw-navy/10 p-6 sm:p-8 space-y-6"
      >
        {/* Basics */}
        <div>
          <span className="lw-eyebrow">About you</span>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <NumberInput
              label="Your age"
              value={form.age}
              onChange={set("age")}
              min={0}
              max={100}
              testId="rec-age"
              error={errors.age}
            />
            <SelectInput
              label="Marital status"
              value={form.marital_status}
              onChange={set("marital_status")}
              options={MARITAL}
              testId="rec-marital"
            />
            <NumberInput
              label="# of dependents"
              value={form.dependents}
              onChange={set("dependents")}
              min={0}
              max={20}
              testId="rec-dependents"
              error={errors.dependents}
            />
            <NumberInput
              label="Annual household income"
              value={form.annual_income}
              onChange={set("annual_income")}
              prefix="$"
              min={0}
              testId="rec-income"
              error={errors.annual_income}
            />
          </div>
        </div>

        {/* Career */}
        <div>
          <span className="lw-eyebrow">Career & employment</span>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <SelectInput
              label="Employment type"
              value={form.employment_type}
              onChange={set("employment_type")}
              options={EMPLOYMENT}
              testId="rec-employment"
            />
            <SelectInput
              label="Profession"
              value={form.profession}
              onChange={set("profession")}
              options={PROFESSION}
              testId="rec-profession"
            />
            <SelectInput
              label="Business partners?"
              value={form.has_business_partners}
              onChange={set("has_business_partners")}
              options={PARTNERS}
              testId="rec-partners"
            />
            <SelectInput
              label="Approx household net worth"
              value={form.net_worth_bucket}
              onChange={set("net_worth_bucket")}
              options={NET_WORTH}
              testId="rec-net-worth"
            />
          </div>
        </div>

        {/* Goals */}
        <div>
          <span className="lw-eyebrow">Goals & comfort</span>
          <div className="mt-4 grid grid-cols-1 gap-5">
            <SelectInput
              label="Your primary goal right now"
              value={form.primary_goal}
              onChange={set("primary_goal")}
              options={GOALS}
              testId="rec-goal"
            />
            <SelectInput
              label="Estate planning concern?"
              value={form.estate_planning_concern}
              onChange={set("estate_planning_concern")}
              options={ESTATE}
              testId="rec-estate"
            />
            <RadioGroup
              label="Your risk comfort"
              value={form.risk_tolerance}
              onChange={set("risk_tolerance")}
              options={RISK}
              testId="rec-risk"
            />
            <Toggle
              label="I already have life insurance"
              helper="Helps us adjust recommendations for existing coverage."
              value={form.has_existing_life}
              onChange={set("has_existing_life")}
              testId="rec-existing-life"
            />
          </div>
        </div>

        {serverError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="recommender-submit"
          className="btn-gold w-full sm:w-auto disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={18} /> Calculating…
            </>
          ) : (
            <>See my top 5 matches →</>
          )}
        </button>
      </form>

      {result && (
        <ResultsCard
          source="recommender"
          headline={`Your top ${result.matches.length} match${result.matches.length === 1 ? "" : "es"}`}
          subhead={`Based on your age (${form.age}) and goal of "${
            GOALS.find((g) => g.value === form.primary_goal)?.label
          }".`}
          rows={result.matches.map((m, i) => ({
            label: `#${i + 1} · ${
              products.find((p) => p.id === m.product_id)?.name || m.product_id
            }`,
            value: `Fit score ${m.score}`,
            testId: `match-${m.product_id}`,
          }))}
          assumptions={result.assumptions}
          matchedIds={result.matched_product_ids}
          reasonsByProduct={reasonsByProduct}
          products={products}
          applyQuery={buildApplyQuery({
            source: "recommender",
            age: form.age,
            goal: form.primary_goal,
            products: result.matched_product_ids.join(","),
          })}
          toolSlug="recommender"
          toolInputs={form}
          toolResults={result}
        />
      )}
    </CalculatorShell>
  );
}
